import { modelSlugOf, type Lesson, type ModelCatalogue } from '@vrlearning/lesson-schema';

/**
 * The "Bronnen" card (ADR-043; spec "Privacy and licensing"): who made what
 * a lesson shows and plays, under which licence -- every library model's
 * credit and its sounds' own, and the posters', the 360° photos' and the world's recordings',
 * which until now only had the world's plates. CC-BY asks for it; CC0 does
 * not, and we show it anyway. Shown on the lesson's last step and at the end
 * of a played lesson. In Dutch, as every string the engine draws.
 */

export type Credit = { readonly what: string; readonly credit: string };

/** A world recording as the worlds' manifest names it (render/worlds/manifest.ts). */
export type RecordingCredit = {
  readonly sound?: string | undefined;
  readonly user?: string | undefined;
  readonly licence: string;
};

const LICENCE: Readonly<Record<string, string>> = {
  'CC0-1.0': 'CC0',
  'CC-BY-4.0': 'CC BY 4.0',
  'CC-BY-3.0': 'CC BY 3.0',
};

/** Every credit of a lesson, each once, models first. */
export function lessonCredits(
  lesson: Pick<Lesson, 'objects' | 'scene' | 'steps'>,
  catalogue: ModelCatalogue,
  recordings: readonly RecordingCredit[] = [],
  /** A served world package's own credits (its GLBs, sky, textures, sounds), after the rest. */
  world: readonly Credit[] = [],
): Credit[] {
  const credits: Credit[] = [];
  const seen = new Set<string>();
  const add = (what: string, credit: string | undefined): void => {
    if (!credit || seen.has(credit)) return;
    seen.add(credit);
    credits.push({ what, credit });
  };
  const objects = Object.values(lesson.objects ?? {});
  for (const object of objects) {
    const slug = modelSlugOf(object.kind);
    const facts = slug === undefined ? undefined : catalogue[slug];
    if (facts) add(`3D-model ${facts.name}`, facts.credit);
  }
  // The recordings the models play, each credit once however many takes share it.
  for (const object of objects) {
    const slug = modelSlugOf(object.kind);
    const facts = slug === undefined ? undefined : catalogue[slug];
    for (const sound of facts?.sounds ?? []) add(`Geluid ${facts!.name}`, sound.credit);
  }
  for (const object of objects) {
    if (object.credit) add(`Foto ${object.label}`, object.credit);
  }
  if (lesson.scene.type === 'pano360') add('360°-foto', lesson.scene.credit);
  for (const step of lesson.steps) add('360°-foto', step.pano?.credit);
  for (const recording of recordings) {
    if (!recording.sound || !recording.user) continue;
    add(
      'Geluid',
      `"${recording.sound}" door ${recording.user}, ${LICENCE[recording.licence] ?? recording.licence}`,
    );
  }
  for (const entry of world) add(entry.what, entry.credit);
  return credits;
}

/** The card's text: one line per credit, as the panel wraps it. */
export function creditsText(credits: readonly Credit[]): string {
  if (credits.length === 0) return 'Alles in deze les is door ons zelf gemaakt.';
  return credits.map((entry) => `${entry.what}: ${entry.credit}`).join(' · ');
}

export const CREDITS_TEXT = { title: 'Bronnen', button: 'Bronnen', close: '◀ Sluiten' } as const;
